/**
 * Session Context
 * Provides reading-session–related state and actions (navigation, progress, annotations),
 * backed by ProgressManager and useChapterNavigation.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useDocumentContext } from './DocumentContext';
import { ReadingSessionInitResult } from '../services/ProgressManager';
import { ProgressData } from '../services/ProgressState';
import { useUserManagement } from '../features/UserManagement/components/UserManagementContext';
import ContinueReadingNotification from './ContinueReadingNotification';
import { TOCNode } from '../types/DocumentTypes';
import {
  NavigationState,
  useChapterNavigation,
  UseChapterNavigationResult,
} from '../services/useChapterNavigation';
import { useProgressTracking } from '../services/useProgressTracking';
import useAnnotations, {
  AnnotationsHookResult,
} from '../features/Annotation/hooks/useAnnotations';

type SessionContextState = 'loading' | 'ready' | 'error';

type SessionContextType = AnnotationsHookResult &
  Omit<UseChapterNavigationResult, 'state'> & {
    navigationState: NavigationState;
  } & {
    state: SessionContextState;
    // Progress management
    progressData: ProgressData;
    trackProgress: () => void;
    initReadingSession: (
      chapters: TOCNode[],
      searchParams: URLSearchParams,
    ) => Promise<ReadingSessionInitResult>;
  };

const SessionContext = createContext<SessionContextType | null>(null);

export function SessionContextProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { id, productMeta, chapters } = useDocumentContext();
  const { identity } = useUserManagement();
  const [state, setState] = useState<SessionContextState>('loading');

  const onAnnotationsLoadedCallback = useCallback(() => {
    setState('ready');
  }, []);
  const annotationsHookResult = useAnnotations({
    isbn: id,
    onAnnotationsLoaded: onAnnotationsLoadedCallback,
  });

  // Use the new progress tracking hook
  const {
    progressData,
    modalState,
    trackProgress,
    initReadingSession,
    handleModalClose,
    handleContinueReading: hookHandleContinueReading,
  } = useProgressTracking(
    identity?.user?.sub ? productMeta?.isbn : undefined,
    identity?.actions?.getAccessToken ?? (async () => undefined),
    productMeta?.blockCount,
  );

  // Ensure tracking refresh on navigation
  const onNavigated = useCallback(() => {
    trackProgress();
  }, [trackProgress]);

  const {
    state: navigationState,
    currentChapter,
    loadedSection,
    htmlRange,
    navigateToChapter,
  } = useChapterNavigation({
    chapters: chapters ?? undefined,
    isbn: id,
    getAccessToken:
      identity?.actions?.getAccessToken ?? (async () => undefined),
    onNavigated,
  });

  // Modal handlers
  const handleContinueReading = useCallback(async () => {
    if (!chapters) {
      return;
    }

    try {
      const result = await hookHandleContinueReading(chapters);
      await navigateToChapter({ chapter: result.chapter });
      // Start or refresh tracking after user chose to continue
      trackProgress();
    } catch (_error) {
      // Silently handle errors - user can retry by clicking continue again
      console.info('Continue reading failed', _error);
    }
  }, [chapters, navigateToChapter, hookHandleContinueReading, trackProgress]);

  const contextValue = useMemo(
    () => ({
      state,
      progressData,
      ...annotationsHookResult,
      trackProgress,
      initReadingSession,
      navigateToChapter,
      currentChapter,
      htmlRange,
      loadedSection,
      navigationState,
    }),
    [
      state,
      progressData,
      annotationsHookResult,
      trackProgress,
      initReadingSession,
      navigateToChapter,
      currentChapter,
      htmlRange,
      loadedSection,
      navigationState,
    ],
  );

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
      <ContinueReadingNotification
        open={modalState.show}
        chapterTitle={modalState.data?.targetChapter?.title}
        onContinue={handleContinueReading}
        onClose={handleModalClose}
      />
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error(
      'useSessionContext must be used within a SessionContextProvider',
    );
  }

  return context;
}
